'use client';

import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import { PR_V2_VIEW_PREFERENCES_STORAGE_KEY } from './pr-v2-view-preferences';

import { safeLocalStorage } from '@/lib/safe-storage';

export const PR_V2_DIFF_STYLE_OPTIONS = ['unified', 'split'] as const;
export const PR_V2_OVERFLOW_OPTIONS = ['scroll', 'wrap'] as const;
export const PR_V2_DIFF_INDICATOR_OPTIONS = ['bars', 'classic', 'none'] as const;
export const PR_V2_LINE_DIFF_OPTIONS = ['word', 'word-alt', 'word-line', 'char', 'none'] as const;
export const PR_V2_HUNK_SEPARATOR_OPTIONS = ['line-info', 'simple'] as const;
// PR diffs are presented in dark mode only; syntax themes exposed in Diff settings are dark variants.
export const PR_V2_HIGHLIGHT_THEME_OPTIONS = [
  'pierre-dark',
  'andromeeda',
  'aurora-x',
  'ayu-dark',
  'ayu-mirage',
  'catppuccin-frappe',
  'catppuccin-macchiato',
  'catppuccin-mocha',
  'dark-plus',
  'dracula',
  'dracula-soft',
  'everforest-dark',
  'github-dark',
  'github-dark-default',
  'github-dark-dimmed',
  'github-dark-high-contrast',
  'gruvbox-dark-hard',
  'gruvbox-dark-medium',
  'gruvbox-dark-soft',
  'horizon',
  'houston',
  'kanagawa-dragon',
  'kanagawa-wave',
  'laserwave',
  'material-theme',
  'material-theme-darker',
  'material-theme-ocean',
  'material-theme-palenight',
  'min-dark',
  'monokai',
  'night-owl',
  'nord',
  'one-dark-pro',
  'plastic',
  'poimandres',
  'red',
  'rose-pine',
  'rose-pine-moon',
  'slack-dark',
  'solarized-dark',
  'synthwave-84',
  'tokyo-night',
  'vesper',
  'vitesse-black',
  'vitesse-dark',
] as const;

export type PrV2DiffStyleOption = (typeof PR_V2_DIFF_STYLE_OPTIONS)[number];
export type PrV2OverflowOption = (typeof PR_V2_OVERFLOW_OPTIONS)[number];
export type PrV2DiffIndicatorOption = (typeof PR_V2_DIFF_INDICATOR_OPTIONS)[number];
export type PrV2LineDiffOption = (typeof PR_V2_LINE_DIFF_OPTIONS)[number];
export type PrV2HunkSeparatorOption = (typeof PR_V2_HUNK_SEPARATOR_OPTIONS)[number];
export type PrV2HighlightThemeOption = (typeof PR_V2_HIGHLIGHT_THEME_OPTIONS)[number];

const PR_V2_LEGACY_HUNK_SEPARATOR_OPTIONS = ['line-info', 'metadata', 'simple'] as const;
type PrV2LegacyHunkSeparatorOption = (typeof PR_V2_LEGACY_HUNK_SEPARATOR_OPTIONS)[number];

export type PrV2DiffPreferences = {
  diffStyle: PrV2DiffStyleOption;
  overflow: PrV2OverflowOption;
  diffIndicators: PrV2DiffIndicatorOption;
  lineDiffType: PrV2LineDiffOption;
  hunkSeparators: PrV2HunkSeparatorOption;
  highlightTheme: PrV2HighlightThemeOption;
  showLineNumbers: boolean;
  expandUnchanged: boolean;
  collapseAllChangedFiles: boolean;
  hideTestFiles: boolean;
};

export type PrV2ViewPreferences = PrV2DiffPreferences & {
  rightSidebarOpen: boolean;
  fileTreeWidthPx: number;
};

export const DEFAULT_PR_V2_DIFF_PREFERENCES: PrV2DiffPreferences = {
  diffStyle: 'unified',
  overflow: 'wrap',
  diffIndicators: 'none',
  lineDiffType: 'word-alt',
  hunkSeparators: 'line-info',
  highlightTheme: 'pierre-dark',
  showLineNumbers: true,
  expandUnchanged: false,
  collapseAllChangedFiles: false,
  hideTestFiles: true,
};

export const DEFAULT_PR_V2_VIEW_PREFERENCES: PrV2ViewPreferences = {
  ...DEFAULT_PR_V2_DIFF_PREFERENCES,
  rightSidebarOpen: true,
  fileTreeWidthPx: 272,
};

const LEGACY_COLLAPSE_ALL_CHANGED_FILES_DEFAULT = false;

const PR_V2_DIFF_PREFERENCE_KEYS = Object.keys(
  DEFAULT_PR_V2_DIFF_PREFERENCES,
) as (keyof PrV2DiffPreferences)[];

export function normalizePrV2HunkSeparatorOption(
  value: PrV2LegacyHunkSeparatorOption,
): PrV2HunkSeparatorOption {
  return value === 'simple' ? 'simple' : 'line-info';
}

type PrV2ViewPreferencesState = PrV2ViewPreferences & {
  applyPreferences: (next: Partial<PrV2ViewPreferences>) => void;
  setRightSidebarOpen: (open: boolean) => void;
  resetDiffPreferences: () => void;
  migrateLegacyPreferences: () => void;
};

const LEGACY_STORAGE_KEYS = {
  diffStyle: 'pr-v2-pierre-diff-style',
  overflow: 'pr-v2-pierre-overflow',
  diffIndicators: 'pr-v2-pierre-diff-indicators',
  lineDiffType: 'pr-v2-pierre-line-diff-type',
  hunkSeparators: 'pr-v2-pierre-hunk-separators',
  themeType: 'pr-v2-pierre-theme-type',
  highlightTheme: 'pr-v2-pierre-highlight-theme',
  showLineNumbers: 'pr-v2-pierre-show-line-numbers',
  expandUnchanged: 'pr-v2-pierre-expand-unchanged',
} as const;

/** Removed preference; still cleared when migrating legacy flat keys. */
const LEGACY_SHOW_TOKEN_HOVER_KEY = 'pr-v2-pierre-show-token-hover';

type OptionList<T extends string> = readonly [T, ...T[]];

function asOption<T extends string>(value: string | null, options: OptionList<T>, fallback: T): T {
  if (value && options.includes(value as T)) {
    return value as T;
  }

  return fallback;
}

function asBoolean(value: string | null, fallback: boolean): boolean {
  if (value === 'true') {
    return true;
  }
  if (value === 'false') {
    return false;
  }

  return fallback;
}

function readLegacyPreferences(storage: Storage): Partial<PrV2DiffPreferences> {
  return {
    diffStyle: asOption(
      storage.getItem(LEGACY_STORAGE_KEYS.diffStyle),
      PR_V2_DIFF_STYLE_OPTIONS,
      DEFAULT_PR_V2_DIFF_PREFERENCES.diffStyle,
    ),
    overflow: asOption(
      storage.getItem(LEGACY_STORAGE_KEYS.overflow),
      PR_V2_OVERFLOW_OPTIONS,
      DEFAULT_PR_V2_DIFF_PREFERENCES.overflow,
    ),
    diffIndicators: asOption(
      storage.getItem(LEGACY_STORAGE_KEYS.diffIndicators),
      PR_V2_DIFF_INDICATOR_OPTIONS,
      DEFAULT_PR_V2_DIFF_PREFERENCES.diffIndicators,
    ),
    lineDiffType: asOption(
      storage.getItem(LEGACY_STORAGE_KEYS.lineDiffType),
      PR_V2_LINE_DIFF_OPTIONS,
      DEFAULT_PR_V2_DIFF_PREFERENCES.lineDiffType,
    ),
    hunkSeparators: normalizePrV2HunkSeparatorOption(
      asOption(
        storage.getItem(LEGACY_STORAGE_KEYS.hunkSeparators),
        PR_V2_LEGACY_HUNK_SEPARATOR_OPTIONS,
        DEFAULT_PR_V2_DIFF_PREFERENCES.hunkSeparators,
      ),
    ),
    highlightTheme: asOption(
      storage.getItem(LEGACY_STORAGE_KEYS.highlightTheme),
      PR_V2_HIGHLIGHT_THEME_OPTIONS,
      DEFAULT_PR_V2_DIFF_PREFERENCES.highlightTheme,
    ),
    showLineNumbers: asBoolean(
      storage.getItem(LEGACY_STORAGE_KEYS.showLineNumbers),
      DEFAULT_PR_V2_DIFF_PREFERENCES.showLineNumbers,
    ),
    expandUnchanged: asBoolean(
      storage.getItem(LEGACY_STORAGE_KEYS.expandUnchanged),
      DEFAULT_PR_V2_DIFF_PREFERENCES.expandUnchanged,
    ),
  };
}

function hasLegacyValues(storage: Storage): boolean {
  return (
    Object.values(LEGACY_STORAGE_KEYS).some((key) => storage.getItem(key) !== null) ||
    storage.getItem(LEGACY_SHOW_TOKEN_HOVER_KEY) !== null
  );
}

function clearLegacyPreferences(storage: Storage): void {
  Object.values(LEGACY_STORAGE_KEYS).forEach((key) => {
    storage.removeItem(key);
  });
  storage.removeItem(LEGACY_SHOW_TOKEN_HOVER_KEY);
}

function normalizePrV2PersistedHighlightTheme(value: unknown): PrV2HighlightThemeOption {
  if (typeof value !== 'string') {
    return DEFAULT_PR_V2_DIFF_PREFERENCES.highlightTheme;
  }

  return (PR_V2_HIGHLIGHT_THEME_OPTIONS as readonly string[]).includes(value)
    ? (value as PrV2HighlightThemeOption)
    : DEFAULT_PR_V2_DIFF_PREFERENCES.highlightTheme;
}

function resetDiffPreferencesInState(
  state: Pick<PrV2ViewPreferences, 'rightSidebarOpen' | 'fileTreeWidthPx'>,
): PrV2ViewPreferences {
  return {
    ...DEFAULT_PR_V2_DIFF_PREFERENCES,
    rightSidebarOpen: state.rightSidebarOpen,
    fileTreeWidthPx: state.fileTreeWidthPx,
  };
}

let migratedLegacyPreferences = false;

export const usePrV2ViewPreferencesStore = create<PrV2ViewPreferencesState>()(
  persist<PrV2ViewPreferencesState>(
    (set) => ({
      ...DEFAULT_PR_V2_DIFF_PREFERENCES,
      // Session-only: PR v2 chooses the chat sidebar state for the active
      // layout and never restores it from storage or cookies (see partialize).
      rightSidebarOpen: DEFAULT_PR_V2_VIEW_PREFERENCES.rightSidebarOpen,
      fileTreeWidthPx: DEFAULT_PR_V2_VIEW_PREFERENCES.fileTreeWidthPx,
      applyPreferences: (next) => set((state) => ({ ...state, ...next })),
      setRightSidebarOpen: (rightSidebarOpen) =>
        set((state) => {
          if (state.rightSidebarOpen === rightSidebarOpen) {
            return state;
          }

          return { rightSidebarOpen };
        }),
      resetDiffPreferences: () => set((state) => resetDiffPreferencesInState(state)),
      migrateLegacyPreferences: () => {
        if (migratedLegacyPreferences || typeof window === 'undefined') {
          return;
        }

        const storage = window.localStorage;
        if (!hasLegacyValues(storage)) {
          return;
        }

        migratedLegacyPreferences = true;

        // Only apply legacy values when the v2 store still has default diff
        // preferences, to avoid overwriting values the user already customised.
        const legacyPreferences = readLegacyPreferences(storage);
        set((state) => {
          const isStillDefault = PR_V2_DIFF_PREFERENCE_KEYS.every(
            (key) => state[key] === DEFAULT_PR_V2_DIFF_PREFERENCES[key],
          );
          if (!isStillDefault) {
            return state;
          }

          return { ...state, ...legacyPreferences };
        });
        clearLegacyPreferences(storage);
      },
    }),
    {
      name: PR_V2_VIEW_PREFERENCES_STORAGE_KEY,
      version: 19,
      migrate: (persistedState, version) => {
        if (!persistedState || typeof persistedState !== 'object') {
          return persistedState as PrV2ViewPreferencesState;
        }

        let nextState = persistedState as Partial<PrV2ViewPreferencesState>;

        if (version < 3) {
          const { themeType: _themeType, ...persistedWithoutThemeType } = nextState as Partial<
            PrV2ViewPreferencesState & { themeType?: string }
          >;

          nextState = {
            ...persistedWithoutThemeType,
            hunkSeparators: normalizePrV2HunkSeparatorOption(
              asOption(
                typeof nextState.hunkSeparators === 'string' ? nextState.hunkSeparators : null,
                PR_V2_LEGACY_HUNK_SEPARATOR_OPTIONS,
                DEFAULT_PR_V2_DIFF_PREFERENCES.hunkSeparators,
              ),
            ),
            collapseAllChangedFiles: LEGACY_COLLAPSE_ALL_CHANGED_FILES_DEFAULT,
          };
        }

        if (version < 4) {
          nextState = {
            ...nextState,
            collapseAllChangedFiles: LEGACY_COLLAPSE_ALL_CHANGED_FILES_DEFAULT,
          };
        }

        if (version < 6) {
          nextState = {
            ...nextState,
            fileTreeWidthPx: DEFAULT_PR_V2_VIEW_PREFERENCES.fileTreeWidthPx,
          };
        }

        if (version < 9) {
          const { themeType: _themeType, ...persistedWithoutThemeType } = nextState as Partial<
            PrV2ViewPreferencesState & { themeType?: string }
          >;

          nextState = {
            ...persistedWithoutThemeType,
            highlightTheme: DEFAULT_PR_V2_DIFF_PREFERENCES.highlightTheme,
          };
        }

        // Older snapshots used this setting to toggle story mode's collapsed-file behavior.
        if (version < 10) {
          nextState = {
            ...nextState,
            collapseAllChangedFiles: DEFAULT_PR_V2_DIFF_PREFERENCES.collapseAllChangedFiles,
          };
        }

        // Diff settings only offer dark syntax themes; remap persisted light themes.
        if (version < 11) {
          nextState = {
            ...nextState,
            highlightTheme: normalizePrV2PersistedHighlightTheme(nextState.highlightTheme),
          };
        }

        // Alt+click token chat used a removed preference (`showTokenHover`); strip it from persisted state.
        if (version < 13) {
          const stripped = { ...(nextState as Record<string, unknown>) };
          delete stripped.showTokenHover;
          nextState = stripped as Partial<PrV2ViewPreferencesState>;
        }

        // Story mode no longer auto-collapses files, and the toggle was removed.
        if (version < 14) {
          nextState = {
            ...nextState,
            collapseAllChangedFiles: DEFAULT_PR_V2_DIFF_PREFERENCES.collapseAllChangedFiles,
          };
        }

        // The chat sidebar is no longer remembered (it always starts open), so
        // drop the value old snapshots persisted — many were poisoned to false
        // by the shared legacy 'sidebar:state' cookie the inbox layout wrote.
        if (version < 15) {
          nextState = {
            ...nextState,
            rightSidebarOpen: DEFAULT_PR_V2_VIEW_PREFERENCES.rightSidebarOpen,
          };
        }

        if (version < 17) {
          const stripped = { ...(nextState as Record<string, unknown>) };
          delete stripped.groupGuidePlacement;
          nextState = stripped as Partial<PrV2ViewPreferencesState>;
        }

        // Test files are now hidden by default. Earlier snapshots persisted the old
        // default (shown) whether or not the user chose it, so move everyone over once.
        if (version < 18) {
          nextState = {
            ...nextState,
            hideTestFiles: DEFAULT_PR_V2_DIFF_PREFERENCES.hideTestFiles,
          };
        }

        // Inline diffs now default to word-alt. Earlier snapshots persisted the old
        // default (word) whether or not the user chose it, so move everyone over once.
        if (version < 19 && nextState.lineDiffType === 'word') {
          nextState = {
            ...nextState,
            lineDiffType: DEFAULT_PR_V2_DIFF_PREFERENCES.lineDiffType,
          };
        }

        return nextState as PrV2ViewPreferencesState;
      },
      storage: createJSONStorage(() => safeLocalStorage),
      // The chat sidebar is session-only; keep it out of the persisted snapshot
      // so layout and explicit user actions can choose it for each PR view.
      partialize: (state) => {
        const { rightSidebarOpen: _rightSidebarOpen, ...persisted } = state;
        return persisted as PrV2ViewPreferencesState;
      },
      onRehydrateStorage: () => (state) => {
        state?.migrateLegacyPreferences();
      },
    },
  ),
);
